import { Link } from 'react-router-dom';
import type { GameMeta } from '@/contracts/game-module';
import { useI18n } from '@/i18n';
import { cn } from '@/modules/shared/cn';

export function GameCard({ game }: { game: GameMeta }) {
  const { t } = useI18n();
  const available = game.status === 'available';

  const content = (
    <article
      className={cn(
        'relative flex gap-4 rounded-2xl border border-dp-line/70 bg-dp-surface/90 p-4 shadow-sm transition',
        available && 'hover:border-dp-accent hover:shadow-md active:scale-[0.99]',
        !available && 'opacity-60',
      )}
    >
      <div
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-dp-tile text-3xl"
        aria-hidden
      >
        {game.icon}
      </div>
      <div className="min-w-0 flex-1 text-left">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-lg font-bold text-dp-ink">{t(game.titleKey)}</h2>
          {available && (
            <span className="rounded-full bg-dp-accent/15 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-dp-accent">
              {t('newBadge')}
            </span>
          )}
          {!available && (
            <span className="rounded-full bg-dp-line/50 px-2 py-0.5 text-[10px] font-bold uppercase text-dp-muted">
              {t('comingSoon')}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm leading-snug text-dp-muted">{t(game.descriptionKey)}</p>
        {available && (
          <span className="mt-2 inline-block text-sm font-bold text-dp-accent">{t('playNow')} →</span>
        )}
      </div>
    </article>
  );

  if (!available) {
    return <div aria-disabled>{content}</div>;
  }

  return (
    <Link to={game.path} className="block outline-none focus-visible:ring-2 focus-visible:ring-dp-accent">
      {content}
    </Link>
  );
}
